import { Fragment } from "react";

import {
  isInactivePlaylistEntry,
  isReadOnlyPlaylistEntry,
} from "../utils/playlistDashboard";

import ResellerManagedBadge from "./ResellerManagedBadge";

const providerAccessFailureStatuses = new Set([
  "provider_access_rejected",
  "provider_access_expired",
  "provider_access_unavailable",
]);

/**
 * Renders a playlist-level warning when its provider access is unavailable.
 *
 * @param {{ playlists?: Array<{ providerAccessStatus?: string }>, status?: string }} props
 * @returns {import("react").ReactElement|null}
 */
const ProviderAccessBadge = ({ playlists = [], status }) => {
  const hasProviderAccessFailure = status
    ? providerAccessFailureStatuses.has(status)
    : playlists.some((playlist) =>
        providerAccessFailureStatuses.has(playlist.providerAccessStatus)
      );

  if (!hasProviderAccessFailure) return null;

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-500/15 dark:text-red-300"
      title="Provider access is unavailable. Contact your provider for more information."
    >
      <i className="fas fa-exclamation-triangle text-[9px]" />
      Provider Access Unavailable
    </span>
  );
};

const formatShortDate = (input) => {
  if (!input) return "";
  const date = input instanceof Date ? input : new Date(input);

  if (Number.isNaN(date.getTime())) return String(input);
  const yy = String(date.getFullYear()).slice(-2);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${mm}/${dd}/${yy}`;
};

const EmptyCell = ({ className = "" }) => (
  <span className={`text-xs text-gray-400 ${className}`}>—</span>
);

const readOnlyTitle =
  "Managed by your reseller. Contact your reseller to make changes.";

const ManagedAccessBadges = ({ entry }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <ResellerManagedBadge size="sm" title={readOnlyTitle} />
    {isInactivePlaylistEntry(entry) && (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-500/15 dark:text-red-300"
        title="Your reseller's LunaKey for this playlist is no longer active. Contact your reseller for more information."
      >
        <i className="fas fa-ban text-[9px]" />
        Inactive
      </span>
    )}
  </div>
);

/**
 * Expiry for a table row. An inactive reseller-managed row has no entitlement,
 * so it shows an empty cell rather than a misleading "Never".
 *
 * @param {{ entry: { expiresAt?: string|null, status?: string }, readOnly: boolean }} props
 * @returns {import("react").ReactNode}
 */
const ExpiresValue = ({ entry, readOnly }) => {
  if (readOnly && isInactivePlaylistEntry(entry)) return <EmptyCell />;

  return entry.expiresAt ? formatShortDate(entry.expiresAt) : "Never";
};

const ReadOnlyActions = () => (
  <span className="text-gray-400 dark:text-gray-500" title={readOnlyTitle}>
    <i aria-hidden="true" className="fas fa-lock" />
    <span className="sr-only">Read-only, managed by your reseller</span>
  </span>
);

const PlaylistManagerLink = ({
  children,
  className,
  navigate,
  playlist,
  readOnly,
  title,
}) => {
  if (readOnly) {
    return (
      <span className={`block ${className}`} title={title}>
        {children}
      </span>
    );
  }

  return (
    <button
      className={`${className} hover:text-primary-600 dark:hover:text-primary-400 transition-colors`}
      title={title}
      type="button"
      onClick={() => navigate(`/playlist-manager/${playlist.id}`)}
    >
      {children}
    </button>
  );
};

const LunaKeyButton = ({
  account,
  isCodeExpired,
  onActivateExpiredCode,
  onShowCodeDetail,
  readOnly,
}) => {
  const representativePlaylist = account.playlists?.[0];

  if (readOnly) {
    if (!account.codeString || !representativePlaylist) return <EmptyCell />;

    return (
      <button
        className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
        title="View reseller LunaKey"
        type="button"
        onClick={() => onShowCodeDetail(representativePlaylist)}
      >
        <i className="fas fa-ticket-alt text-xl" />
      </button>
    );
  }

  if (!account.codeString) {
    return (
      <button
        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400 transition-colors"
        title="No code - Click to generate"
        type="button"
        onClick={() => {
          if (!representativePlaylist) return;
          onActivateExpiredCode({
            ...representativePlaylist,
            newCode: "",
            pendingCodeGeneration: true,
          });
        }}
      >
        <i className="fas fa-ticket-alt text-xl opacity-50" />
      </button>
    );
  }

  if (isCodeExpired(account.codeString)) {
    return (
      <button
        className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors"
        title="Code expired - Click to generate new code"
        type="button"
        onClick={() => onActivateExpiredCode(representativePlaylist)}
      >
        <i className="fas fa-ticket-alt text-xl" />
      </button>
    );
  }

  return (
    <button
      className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
      title="View active LunaKey"
      type="button"
      onClick={() => onShowCodeDetail(representativePlaylist)}
    >
      <i className="fas fa-ticket-alt text-xl" />
    </button>
  );
};

const AutoRenewCell = ({
  account,
  hideModuleActions,
  onAutoRenewToggle,
  readOnly,
}) => {
  if (hideModuleActions) return null;

  const representativePlaylist = account.playlists?.[0];
  const canToggle =
    !readOnly &&
    account.redeemType === "single-use" &&
    account.expiresAt &&
    representativePlaylist;

  return (
    <td className="px-4 py-4 text-center whitespace-nowrap">
      {canToggle ? (
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            checked={account.autoRenew || false}
            className="sr-only peer"
            type="checkbox"
            onChange={(event) =>
              onAutoRenewToggle(representativePlaylist.id, event.target.checked)
            }
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600" />
        </label>
      ) : (
        <EmptyCell />
      )}
    </td>
  );
};

const DevicesButton = ({ account, onShowDevices, representativePlaylist }) => (
  <button
    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50 transition-colors"
    disabled={!account.devicesCount}
    title={
      account.devicesCount
        ? "View devices logged in on this account"
        : "No active devices"
    }
    type="button"
    onClick={() => onShowDevices(representativePlaylist)}
  >
    <i className="fas fa-tv text-xs" />
    {account.devicesCount || 0}
  </button>
);

const getPlaylistNameSummary = (account) => {
  const names = (account.playlists || [])
    .map((playlist) => playlist.playlistName)
    .filter(Boolean);

  if (names.length === 0) return "Multiple playlists";

  return names.join(", ");
};

const expandedAccentCell =
  "border-l-4 border-cornflower-400 dark:border-cornflower-300";

// Parent row: strongest cornflower wash — clearly the "open" header
const expandedGroupParent =
  "bg-cornflower-100 dark:bg-cornflower-900/60 border-y border-cornflower-400/35 shadow-[0_0_0_1px_rgba(100,149,237,0.22)] [&>td]:bg-inherit";

// Subheader strip: mid-tone bridge between parent and child rows
const expandedGroupSubheader =
  "bg-cornflower-50 dark:bg-cornflower-950/80 border-b border-cornflower-300/25 [&>td]:bg-inherit";

// Child rows: neutral inset so they read as nested detail, not another header
const expandedGroupChild =
  "bg-slate-50 dark:bg-gray-900/90 border-b border-cornflower-200/20 dark:border-cornflower-400/10 hover:bg-cornflower-50/90 dark:hover:bg-cornflower-950/50 [&>td]:bg-inherit";

const expandedGroupChildLast =
  "shadow-[inset_0_-2px_0_0_rgba(100,149,237,0.35)] border-b border-cornflower-400/30";

const expandedChevronOpen =
  "bg-cornflower-500 text-white shadow-md ring-2 ring-cornflower-300/45";

const expandedChevronClosed =
  "text-gray-500 hover:text-cornflower-600 hover:bg-cornflower-400/10 dark:text-gray-400 dark:hover:text-cornflower-300";

const PlaylistGroupSubheader = ({ account, columnCount, isExpanded }) => {
  if (!isExpanded) return null;

  return (
    <tr className={expandedGroupSubheader}>
      <td aria-hidden="true" className={`${expandedAccentCell} px-3 py-0`} />
      <td className="px-4 py-2" colSpan={columnCount - 1}>
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-cornflower-700 dark:text-cornflower-200">
          <i className="fas fa-list-ul text-[9px]" />
          {account.playlistCount} playlists on this LunaKey
        </div>
      </td>
    </tr>
  );
};

const PlaylistChildRow = ({
  playlist,
  hideModuleActions,
  isLastChild,
  navigate,
  onDeletePlaylist,
  onEditPlaylist,
  readOnly,
}) => (
  <tr
    className={`${expandedGroupChild} ${
      isLastChild ? expandedGroupChildLast : ""
    }`}
  >
    <td aria-hidden="true" className={`${expandedAccentCell} px-3 py-3`} />
    <td className="px-4 py-3 pl-6 text-cornflower-500/75 dark:text-cornflower-300/85">
      <span
        aria-hidden="true"
        className="inline-flex h-full min-h-[1.25rem] items-center border-l border-cornflower-300/45 pl-2 text-xs"
      >
        ↳
      </span>
    </td>
    <td className="px-4 py-3">
      <div className="flex flex-col items-start gap-1">
        <PlaylistManagerLink
          className="truncate max-w-[140px] text-left text-sm font-medium text-gray-700 dark:text-gray-200"
          navigate={navigate}
          playlist={playlist}
          readOnly={readOnly}
          title={playlist.playlistName}
        >
          {playlist.playlistName}
        </PlaylistManagerLink>
        <ProviderAccessBadge status={playlist.providerAccessStatus} />
      </div>
    </td>
    <td className="px-4 py-3">
      <PlaylistManagerLink
        className="truncate max-w-[180px] text-left font-mono text-sm text-gray-500 dark:text-gray-400"
        navigate={navigate}
        playlist={playlist}
        readOnly={readOnly}
        title={playlist.domain}
      >
        {playlist.domain}
      </PlaylistManagerLink>
    </td>
    <td className="px-4 py-3">
      <PlaylistManagerLink
        className="truncate max-w-[120px] text-left font-mono text-sm text-gray-500 dark:text-gray-400"
        navigate={navigate}
        playlist={playlist}
        readOnly={readOnly}
        title={playlist.username}
      >
        {playlist.username}
      </PlaylistManagerLink>
    </td>
    <td className="px-3 py-3 text-center">
      <EmptyCell />
    </td>
    <td className="px-3 py-3 text-center">
      <EmptyCell />
    </td>
    <td className="px-4 py-3 font-mono text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
      {formatShortDate(playlist.addedDate)}
    </td>
    <td className="px-4 py-3">
      <EmptyCell />
    </td>
    {!hideModuleActions && (
      <td className="px-4 py-3 text-center">
        <EmptyCell />
      </td>
    )}
    <td className="px-4 py-3 text-center whitespace-nowrap">
      {readOnly ? (
        <ReadOnlyActions />
      ) : (
        <>
          <button
            className="text-blue-500 hover:text-blue-600 mr-3"
            title="Edit playlist"
            type="button"
            onClick={() => onEditPlaylist(playlist)}
          >
            <i className="fas fa-edit" />
          </button>
          <button
            className="text-red-600 hover:text-red-700"
            title="Remove playlist"
            type="button"
            onClick={() => onDeletePlaylist(playlist)}
          >
            <i className="fas fa-trash" />
          </button>
        </>
      )}
    </td>
  </tr>
);

const SingleAccountRow = ({
  account,
  hideModuleActions,
  isCodeExpired,
  navigate,
  onActivateExpiredCode,
  onAutoRenewToggle,
  onDeletePlaylist,
  onEditPlaylist,
  onForceLogout,
  onShowCodeDetail,
  onShowDevices,
}) => {
  const playlist = account.playlists?.[0];

  if (!playlist) return null;

  const readOnly =
    isReadOnlyPlaylistEntry(account) || isReadOnlyPlaylistEntry(playlist);

  return (
    <tr className="bg-white dark:bg-gray-800 hover:bg-[#fafafa] dark:hover:bg-gray-700 transition-all duration-200 group border-b border-gray-100 dark:border-gray-700">
      <td className="px-3 py-4" />
      <td className="px-4 py-4 font-medium text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
        <PlaylistManagerLink
          className="truncate max-w-[140px] text-left"
          navigate={navigate}
          playlist={playlist}
          readOnly={readOnly}
          title={account.displayName}
        >
          {account.displayName}
        </PlaylistManagerLink>
        {readOnly && (
          <div className="mt-1">
            <ManagedAccessBadges entry={account} />
          </div>
        )}
      </td>
      <td className="px-4 py-4 text-gray-600 dark:text-gray-300">
        <div className="flex flex-col items-start gap-1">
          <div className="truncate max-w-[140px]" title={playlist.playlistName}>
            {playlist.playlistName}
          </div>
          <ProviderAccessBadge status={playlist.providerAccessStatus} />
        </div>
      </td>
      <td className="px-4 py-4">
        <PlaylistManagerLink
          className="truncate max-w-[180px] text-left font-mono text-sm text-gray-500 dark:text-gray-400"
          navigate={navigate}
          playlist={playlist}
          readOnly={readOnly}
          title={playlist.domain}
        >
          {playlist.domain}
        </PlaylistManagerLink>
      </td>
      <td className="px-4 py-4">
        <PlaylistManagerLink
          className="truncate max-w-[120px] text-left font-mono text-sm text-gray-500 dark:text-gray-400"
          navigate={navigate}
          playlist={playlist}
          readOnly={readOnly}
          title={playlist.username}
        >
          {playlist.username}
        </PlaylistManagerLink>
      </td>
      <td className="px-3 py-4 text-center">
        {readOnly ? (
          <EmptyCell />
        ) : (
          <DevicesButton
            account={account}
            representativePlaylist={playlist}
            onShowDevices={onShowDevices}
          />
        )}
      </td>
      <td className="px-3 py-4 text-center">
        <LunaKeyButton
          account={account}
          isCodeExpired={isCodeExpired}
          readOnly={readOnly}
          onActivateExpiredCode={onActivateExpiredCode}
          onShowCodeDetail={onShowCodeDetail}
        />
      </td>
      <td className="px-4 py-4 font-mono text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {formatShortDate(playlist.addedDate)}
      </td>
      <td className="px-4 py-4 font-mono text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
        <ExpiresValue entry={account} readOnly={readOnly} />
      </td>
      <AutoRenewCell
        account={account}
        hideModuleActions={hideModuleActions}
        readOnly={readOnly}
        onAutoRenewToggle={onAutoRenewToggle}
      />
      <td className="px-4 py-4 text-center whitespace-nowrap">
        {readOnly ? (
          <ReadOnlyActions />
        ) : (
          <>
            <button
              className="text-blue-500 hover:text-blue-600 mr-3"
              title="Edit playlist"
              type="button"
              onClick={() => onEditPlaylist(playlist)}
            >
              <i className="fas fa-edit" />
            </button>
            <button
              className="text-amber-500 hover:text-amber-600 mr-3"
              title="Force logout app sessions for this account"
              type="button"
              onClick={() => onForceLogout(playlist)}
            >
              <i className="fas fa-sign-out-alt" />
            </button>
            <button
              className="text-red-600 hover:text-red-700"
              title="Remove playlist"
              type="button"
              onClick={() => onDeletePlaylist(playlist)}
            >
              <i className="fas fa-trash" />
            </button>
          </>
        )}
      </td>
    </tr>
  );
};

const MultiAccountRow = ({
  account,
  columnCount,
  expandedAccountKeys,
  hideModuleActions,
  isCodeExpired,
  navigate,
  onActivateExpiredCode,
  onAutoRenewToggle,
  onDeletePlaylist,
  onEditPlaylist,
  onForceLogout,
  onShowCodeDetail,
  onShowDevices,
  onToggleExpanded,
}) => {
  const isExpanded = expandedAccountKeys.has(account.rowKey);
  const representativePlaylist = account.playlists?.[0];
  const playlistTotal = account.playlists?.length || 0;

  if (!representativePlaylist) return null;

  const readOnly =
    isReadOnlyPlaylistEntry(account) ||
    account.playlists.some((playlist) => isReadOnlyPlaylistEntry(playlist));

  const toggleExpanded = () => onToggleExpanded(account.rowKey);

  return (
    <Fragment key={account.rowKey}>
      <tr
        className={`transition-colors ${
          isExpanded
            ? expandedGroupParent
            : "bg-white dark:bg-gray-800 hover:bg-[#fafafa] dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 [&>td]:bg-inherit"
        }`}
      >
        <td
          className={`px-3 py-4 text-center ${
            isExpanded ? expandedAccentCell : ""
          }`}
        >
          <button
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse playlists" : "Expand playlists"}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-all ${
              isExpanded ? expandedChevronOpen : expandedChevronClosed
            }`}
            type="button"
            onClick={toggleExpanded}
          >
            <i
              className={`fas fa-chevron-${isExpanded ? "down" : "right"} text-xs`}
            />
          </button>
        </td>
        <td className="px-4 py-4 font-medium text-gray-900 dark:text-gray-100">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate max-w-[160px]" title={account.displayName}>
              {account.displayName}
            </div>
            {isExpanded && (
              <span className="inline-flex items-center gap-1 rounded-full bg-cornflower-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm motion-reduce:animate-none">
                <span className="relative flex h-1.5 w-1.5 motion-reduce:hidden">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                </span>
                Open
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {readOnly ? (
              <ManagedAccessBadges entry={account} />
            ) : (
              <div
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  account.playlistLimitReached
                    ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                    : isExpanded
                      ? "bg-cornflower-200/85 text-cornflower-800 dark:bg-cornflower-500/20 dark:text-cornflower-100"
                      : "bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400"
                }`}
              >
                <i className="fas fa-layer-group text-[9px]" />
                {account.playlistCount}/{account.maxPlaylistsPerAccount}
              </div>
            )}
          </div>
        </td>
        <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
          {isExpanded ? (
            <span className="text-xs italic text-cornflower-700/85 dark:text-cornflower-200/90">
              See playlists below
            </span>
          ) : (
            <div className="flex flex-col items-start gap-1">
              <div
                className="truncate max-w-[160px]"
                title={getPlaylistNameSummary(account)}
              >
                {getPlaylistNameSummary(account)}
              </div>
              <ProviderAccessBadge playlists={account.playlists} />
            </div>
          )}
        </td>
        <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
          {isExpanded ? <EmptyCell /> : `${account.playlistCount} providers`}
        </td>
        <td className="px-4 py-4">
          <EmptyCell />
        </td>
        <td className="px-3 py-4 text-center">
          {readOnly ? (
            <EmptyCell />
          ) : (
            <DevicesButton
              account={account}
              representativePlaylist={representativePlaylist}
              onShowDevices={onShowDevices}
            />
          )}
        </td>
        <td className="px-3 py-4 text-center">
          <LunaKeyButton
            account={account}
            isCodeExpired={isCodeExpired}
            readOnly={readOnly}
            onActivateExpiredCode={onActivateExpiredCode}
            onShowCodeDetail={onShowCodeDetail}
          />
        </td>
        <td className="px-4 py-4 font-mono text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
          {formatShortDate(account.addedDate)}
        </td>
        <td className="px-4 py-4 font-mono text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
          <ExpiresValue entry={account} readOnly={readOnly} />
        </td>
        <AutoRenewCell
          account={account}
          hideModuleActions={hideModuleActions}
          readOnly={readOnly}
          onAutoRenewToggle={onAutoRenewToggle}
        />
        <td className="px-4 py-4 text-center whitespace-nowrap">
          {readOnly ? (
            <ReadOnlyActions />
          ) : (
            <button
              className="text-amber-500 hover:text-amber-600"
              title="Force logout app sessions for this account"
              type="button"
              onClick={() => onForceLogout(representativePlaylist)}
            >
              <i className="fas fa-sign-out-alt" />
            </button>
          )}
        </td>
      </tr>
      <PlaylistGroupSubheader
        account={account}
        columnCount={columnCount}
        isExpanded={isExpanded}
      />
      {isExpanded &&
        account.playlists.map((playlist, index) => (
          <PlaylistChildRow
            hideModuleActions={hideModuleActions}
            isLastChild={index === playlistTotal - 1}
            key={`${account.rowKey}:${playlist.id}`}
            navigate={navigate}
            playlist={playlist}
            readOnly={readOnly || isReadOnlyPlaylistEntry(playlist)}
            onDeletePlaylist={onDeletePlaylist}
            onEditPlaylist={onEditPlaylist}
          />
        ))}
    </Fragment>
  );
};

const AccountGroupRow = (props) => {
  const { account } = props;

  if (account.isMultiPlaylist) {
    return <MultiAccountRow {...props} />;
  }

  return <SingleAccountRow {...props} />;
};

const PlaylistAccountsTable = ({
  accountGroups,
  expandedAccountKeys,
  hideModuleActions,
  isCodeExpired,
  navigate,
  onActivateExpiredCode,
  onAutoRenewToggle,
  onDeletePlaylist,
  onEditPlaylist,
  onForceLogout,
  onHeaderClick,
  onShowCodeDetail,
  onShowDevices,
  onToggleExpanded,
  searchQuery,
  sortBy,
  sortDirection,
}) => {
  const columnCount = hideModuleActions ? 10 : 11;

  const renderSortIcon = (column) =>
    sortBy === column ? (
      <i
        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
      />
    ) : null;

  const headerClass =
    "px-4 py-4 text-left cursor-pointer hover:bg-primary-100 dark:hover:bg-gray-700 transition-all duration-200 whitespace-nowrap";

  return (
    <div className="overflow-x-auto rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700">
      <table className="w-full min-w-[1100px]">
        <thead className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-gray-700 dark:to-gray-800">
          <tr>
            <th className="px-3 py-4 text-center first:rounded-tl-xl w-10">
              <span className="sr-only">Expand</span>
            </th>
            <th className={headerClass} onClick={() => onHeaderClick("name")}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Customer
                </span>
                {renderSortIcon("name")}
              </div>
            </th>
            <th
              className={headerClass}
              onClick={() => onHeaderClick("playlistName")}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Playlist
                </span>
                {renderSortIcon("playlistName")}
              </div>
            </th>
            <th className={headerClass} onClick={() => onHeaderClick("domain")}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Domain
                </span>
                {renderSortIcon("domain")}
              </div>
            </th>
            <th
              className={headerClass}
              onClick={() => onHeaderClick("username")}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Username
                </span>
                {renderSortIcon("username")}
              </div>
            </th>
            <th
              className="px-3 py-4 text-center cursor-pointer hover:bg-primary-100 dark:hover:bg-gray-700 transition-all duration-200 whitespace-nowrap"
              onClick={() => onHeaderClick("devices")}
            >
              <div className="flex items-center justify-center gap-2">
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Devices
                </span>
                {renderSortIcon("devices")}
              </div>
            </th>
            <th
              className="px-3 py-4 text-center cursor-pointer hover:bg-primary-100 dark:hover:bg-gray-700 transition-all duration-200 whitespace-nowrap"
              onClick={() => onHeaderClick("code")}
            >
              <div className="flex items-center justify-center gap-2">
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  LunaKey
                </span>
                {renderSortIcon("code")}
              </div>
            </th>
            <th className={headerClass} onClick={() => onHeaderClick("added")}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Added
                </span>
                {renderSortIcon("added")}
              </div>
            </th>
            <th
              className={headerClass}
              onClick={() => onHeaderClick("expires")}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Expires
                </span>
                {renderSortIcon("expires")}
              </div>
            </th>
            {!hideModuleActions && (
              <th className="px-4 py-4 text-center whitespace-nowrap">
                <span
                  className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider inline-flex items-center gap-1.5"
                  title="Auto-renew on expiry"
                >
                  <i className="fas fa-sync-alt text-[10px]" />
                  Auto
                </span>
              </th>
            )}
            <th className="px-4 py-4 text-center last:rounded-tr-xl whitespace-nowrap">
              <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {accountGroups.length > 0 ? (
            accountGroups.map((account) => (
              <AccountGroupRow
                account={account}
                columnCount={columnCount}
                expandedAccountKeys={expandedAccountKeys}
                hideModuleActions={hideModuleActions}
                isCodeExpired={isCodeExpired}
                key={account.rowKey}
                navigate={navigate}
                onActivateExpiredCode={onActivateExpiredCode}
                onAutoRenewToggle={onAutoRenewToggle}
                onDeletePlaylist={onDeletePlaylist}
                onEditPlaylist={onEditPlaylist}
                onForceLogout={onForceLogout}
                onShowCodeDetail={onShowCodeDetail}
                onShowDevices={onShowDevices}
                onToggleExpanded={onToggleExpanded}
              />
            ))
          ) : (
            <tr>
              <td className="text-center py-8" colSpan={columnCount}>
                <i className="fas fa-search text-4xl text-gray-300 mb-3" />
                <p className="text-gray-500">
                  No accounts found
                  {searchQuery ? ` matching "${searchQuery}"` : ""}
                </p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PlaylistAccountsTable;
